/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/VirtualEventRegistrationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * @file VirtualEventRegistrationModal
 * Collects full name, country code, and phone before registering for a virtual event.
 * Phone row matches {@link ../EventRegistrationModal/index.jsx} (CountryCodeSelector + input).
 */

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import { ReactComponent as CloseIcon } from "../../assets/icons/Events/CloseIcon.svg";
import CountryCodeSelector from "../../components/CountryCodeSelector";
import {  getCountryCodeOptions  } from "../../data/selectOptions";
import { getSelectedCountryCodeOption } from "../../utils/common.js";
import useDeviceDetection from "../../hooks/useDeviceDetection";

import styles from "./index.module.css";

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function(): void} props.onClose
 * @param {function({ nombre: string, telefono: string, codigoPais: string }): Promise<{ success?: boolean, error?: string }>} props.onSubmit
 */
const VirtualEventRegistrationModal = ({ open, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const dialogRef = useRef(null);
  const { deviceType } = useDeviceDetection();
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  /** ISO-style country code from {@link countryCodeOptions} (e.g. `MX`, `US`). */
  const [codigoPais, setCodigoPais] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const getCountryCodeSelectorWidth = (type) => {
    switch (type) {
      case "laptop":
        return "130%";
      case "tablet":
        return "90%";
      case "smallMobile":
        return "130%";
      case "mediumMobile":
        return "140%";
      default:
        return "115%";
    }
  };

  const trimmedNombre = nombre.trim();
  const trimmedTelefono = telefono.trim();
  const hasCodigoPais =
    codigoPais != null && String(codigoPais).trim().length > 0;
  const canSubmit =
    trimmedNombre.length > 0 &&
    trimmedTelefono.length > 0 &&
    hasCodigoPais &&
    !isSubmitting;

  useEffect(() => {
    const handleDialogClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleDialogClose);
    }

    if (open) {
      setNombre("");
      setTelefono("");
      setCodigoPais(null);
      setSubmitError(null);
      setIsSubmitting(false);
      dialog?.showModal();
    } else {
      dialog?.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleDialogClose);
      }
    };
  }, [open, onClose]);

  const handleRegisterClick = async () => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await onSubmit({
        nombre: trimmedNombre,
        telefono: trimmedTelefono,
        codigoPais: String(codigoPais).trim(),
      });
      if (!result?.success && result?.error) {
        setSubmitError(result.error);
      }
    } catch (e) {
      setSubmitError(t("pages.events.virtualRegisterErrorGeneric"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {open && <div className={styles.overlay} aria-hidden />}
      <dialog ref={dialogRef} className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.closeButtonContainer}>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              disabled={isSubmitting}
              aria-label={t("common.close")}
            >
              <CloseIcon />
            </button>
          </div>
          <div className={styles.modalTitle}>
            {t("components.eventRegistrationModal.header")}
          </div>
          <div className={styles.modalSubtitle}>
            {t("components.eventRegistrationModal.subheader")}
          </div>
        </div>

        <div className={styles.scrollableContent}>
          <label
            className={`${styles.modalSubtitle} ${styles.personalInformationSubtitle}`}
          >
            {t("components.eventRegistrationModal.personalInformation")}
          </label>
          <div className={styles.generalInfoSection}>
            <div className={styles.generalInfoRow}>
              <div className={styles.categoryContainer}>
                <label
                  className={styles.label}
                  htmlFor="virtual-register-nombre"
                >
                  {t("components.eventRegistrationModal.name")}
                </label>
                <input
                  id="virtual-register-nombre"
                  type="text"
                  autoComplete="name"
                  placeholder={t(
                    "components.eventRegistrationModal.namePlaceholder",
                  )}
                  className={styles.input}
                  style={{ borderColor: "#909092" }}
                  disabled={isSubmitting}
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className={styles.categoryContainer}>
                <label
                  className={styles.label}
                  htmlFor="virtual-register-telefono"
                >
                  {t("components.eventRegistrationModal.phone")}
                </label>
                <div className={styles.phoneInputContainer}>
                  <CountryCodeSelector
                    value={getSelectedCountryCodeOption(
                      countryCodeOptions,
                      codigoPais,
                    )}
                    onChange={(selected) => {
                      if (selected) {
                        setCodigoPais(selected.value);
                      }
                    }}
                    isDisabled={isSubmitting}
                    containerWidth={getCountryCodeSelectorWidth(deviceType)}
                    placeholder={t("common.select")}
                  />
                  <input
                    id="virtual-register-telefono"
                    type="tel"
                    inputMode="numeric"
                    className={styles.phoneInput}
                    disabled={isSubmitting}
                    placeholder={t(
                      "components.contactInformation.officePhonePlaceholder",
                    )}
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    style={{
                      borderColor: "#909092",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          {submitError ? (
            <p className={styles.errorMessage} role="alert">
              {submitError}
            </p>
          ) : null}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.modalButtons}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isSubmitting}
            >
              {t("components.eventRegistrationModal.cancelButton")}
            </button>
            <button
              type="button"
              className={styles.createButton}
              onClick={handleRegisterClick}
              disabled={!canSubmit}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("components.eventRegistrationModal.registerButton")
              )}
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default VirtualEventRegistrationModal;
